import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useEffect,
} from "react";
import type { ImportJob, ImportProgressContextType } from "@/lib/types/import-progress";

const ImportProgressContext = createContext<ImportProgressContextType | undefined>(undefined);

const globalActiveJobsRef = { current: new Map<string, ImportJob>() };

export const ImportProgressProvider = ({ children }: { children: ReactNode }) => {
  const [, forceUpdate] = useState(0);

  const triggerUpdate = useCallback(() => {
    forceUpdate((prev) => prev + 1);
  }, []);

  const activeJobs = globalActiveJobsRef.current;

  const addJob = useCallback(
    (jobId: string, listModelType: string) => {
      if (!globalActiveJobsRef.current.has(jobId)) {
        globalActiveJobsRef.current.set(jobId, {
          jobId,
          listModelType,
          startTime: Date.now(),
          status: "running",
        });
        triggerUpdate();
      }
    },
    [triggerUpdate]
  );

  const updateJob = useCallback(
    (jobId: string, updates: Partial<ImportJob>) => {
      const existingJob = globalActiveJobsRef.current.get(jobId);
      if (existingJob) {
        globalActiveJobsRef.current.set(jobId, { ...existingJob, ...updates });
        triggerUpdate();
      }
    },
    [triggerUpdate]
  );

  const removeJob = useCallback(
    (jobId: string) => {
      if (globalActiveJobsRef.current.delete(jobId)) {
        triggerUpdate();
      }
    },
    [triggerUpdate]
  );

  const getJob = useCallback((jobId: string) => {
    return globalActiveJobsRef.current.get(jobId);
  }, []);

  // Auto-cleanup jobs older than 24h that are done/errored
  useEffect(() => {
    const cleanupOldJobs = () => {
      const now = Date.now();
      const maxAge = 24 * 60 * 60 * 1000;
      let hasChanges = false;

      for (const [jobId, job] of globalActiveJobsRef.current) {
        if (
          now - job.startTime > maxAge &&
          (job.status === "completed" || job.status === "error")
        ) {
          globalActiveJobsRef.current.delete(jobId);
          hasChanges = true;
        }
      }

      if (hasChanges) triggerUpdate();
    };

    cleanupOldJobs();
    const interval = setInterval(cleanupOldJobs, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [triggerUpdate]);

  return (
    <ImportProgressContext.Provider value={{ activeJobs, addJob, updateJob, removeJob, getJob }}>
      {children}
    </ImportProgressContext.Provider>
  );
};

export const useImportProgressContext = () => {
  const context = useContext(ImportProgressContext);
  if (!context) {
    throw new Error("useImportProgressContext must be used within ImportProgressProvider");
  }
  return context;
};
